import type { TextLayer } from '../model/types';

/**
 * Text layout shared by the live canvas, the export path and the on-canvas
 * editor, so a line of text lands in the same place in all three.
 *
 * Deliberately simple: no kerning, no bidi, no wrapping decisions here. Wrapping
 * is the editor's job (a textarea wraps); this module turns the resulting lines
 * into positions.
 */

/** CSS font shorthand for a text layer, at its own size. */
export function fontShorthand(
  layer: Pick<TextLayer, 'fontFamily' | 'fontSize' | 'fontWeight' | 'italic'>,
) {
  const style = layer.italic ? 'italic ' : '';
  return `${style}${layer.fontWeight} ${layer.fontSize}px ${layer.fontFamily}`;
}

/**
 * The default line height, in font sizes — the model's one notion of leading.
 * The editor overlay draws its draft with it too, so what you type is the
 * leading you get.
 */
export const DEFAULT_TEXT_LINE_HEIGHT = 1.2;

/** The layer's text split into drawn lines. Empty text still draws one line. */
export function textLines(text: string): string[] {
  const lines = text.split('\n');
  return lines.length > 0 ? lines : [''];
}

export interface TextLayout {
  lines: string[];
  /** Distance between baselines, in document pixels. */
  lineHeight: number;
  /** Baseline of the first line, measured from the layer's top. */
  firstBaseline: number;
  /** Anchor x for each line, given the alignment and wrap width. */
  anchorX: number;
  /** True when lines must be centred or right-aligned against a wrap width. */
  wraps: boolean;
}

export function layoutText(layer: TextLayer): TextLayout {
  const lines = textLines(layer.text);
  const lineHeight = Math.max(1, layer.fontSize * layer.lineHeight);
  return {
    lines,
    lineHeight,
    // Centre the first baseline in the line box so a single line sits where the
    // user clicked rather than hanging below the click point.
    firstBaseline: lineHeight / 2,
    anchorX: layer.align === 'left' ? 0 : layer.align === 'right' ? 1 : 0.5,
    wraps: layer.maxWidth !== null,
  };
}

/** Total drawn height of a text layer, in document pixels. */
export function textHeight(layer: TextLayer): number {
  const layout = layoutText(layer);
  return layout.lines.length * layout.lineHeight;
}

/**
 * Where a text layer's box sits, given the layer's own x/y as its top-left.
 * Used by the editor overlay to line a textarea up with the drawn text.
 */
export function textBox(layer: TextLayer): { width: number; height: number } {
  return {
    width: layer.maxWidth ?? Math.max(1, estimateTextWidth(layer)),
    height: textHeight(layer),
  };
}

/**
 * Rough width for a single line: enough to size the editor box when there is no
 * wrap width. Measured by glyph class rather than a canvas, because this must run
 * outside the engine.
 */
export function estimateTextWidth(layer: TextLayer): number {
  const widest = Math.max(
    1,
    ...textLines(layer.text).map((line) => {
      let units = 0;
      for (const char of line) {
        units += /[\s.,;:'"!|]/.test(char) ? 0.32 : /[MWmw@]/.test(char) ? 0.95 : 0.56;
      }
      return units;
    }),
  );
  return Math.ceil(widest * layer.fontSize * (layer.fontWeight === 'bold' ? 1.06 : 1));
}
